import type { Direction, RectSizeUnit } from '@lighthouse/core'
import { DIRECTION } from '@lighthouse/core'
import { findNodeDom, getFontSizeByPageStyle, PAGE_MODAL } from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useState } from 'react'

import { lastPageOfStackAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'

import { LayoutSizeInput } from './LayoutSizeInput'
import { useSetGapHighlight } from './useSetGapHighlight'

type SizeInputValue = {
    size?: number
    unit?: Exclude<RectSizeUnit, 'vw' | 'vh'>
}
interface GapSizeInputProps {
    value?: SizeInputValue
    onChange: (value: SizeInputValue) => void
    direction: Direction
    disableHighlight?: boolean

    disablePercent?: boolean
    disableRem?: boolean
}

export const GapSizeInput: React.FC<GapSizeInputProps> = ({ value, onChange, direction, disableHighlight, disablePercent, disableRem }) => {
    const [moveOpen, setMoveOpen] = useState(false)
    const { onMouseEnterGap, onMouseLeaveGap } = useSetGapHighlight(disableHighlight || moveOpen)

    const onUnitChange = useAtomCallback(
        useCallback(
            (get, set, unit: Exclude<RectSizeUnit, 'vw' | 'vh'>, prevValue?: SizeInputValue) => {
                const prevUnit = prevValue?.unit || 'px'
                const prevGap = prevValue?.size || 0

                if (prevUnit === unit) {
                    return { size: prevGap, unit }
                }

                const stack = get(lastPageOfStackAtom)
                const pageInternalState = stack?.stackId ? get(pageInternalStateAtomFamily(stack.stackId)) : undefined
                const selectedNode = pageInternalState?.selectedNodes?.[0]

                const targetDom = selectedNode
                    ? findNodeDom(selectedNode, document.querySelector(`.${PAGE_MODAL}`))
                    : findNodeDom({ id: 'root' }, document.querySelector(`.${PAGE_MODAL}`))
                if (!targetDom) {
                    return { size: prevGap, unit }
                }

                const rect = targetDom.getBoundingClientRect()
                const style = window.getComputedStyle(targetDom)
                const paddingLeft = Number.parseFloat(style.paddingLeft)
                const paddingTop = Number.parseFloat(style.paddingTop)
                const paddingRight = Number.parseFloat(style.paddingRight)
                const paddingBottom = Number.parseFloat(style.paddingBottom)
                const contentSize =
                    direction === DIRECTION.horizontal ? rect.width - paddingLeft - paddingRight : rect.height - paddingTop - paddingBottom

                switch (unit) {
                    case '%': {
                        if (prevUnit === 'px') {
                            const gap = Number(((prevGap * 100) / contentSize).toFixed(2))
                            return { size: gap, unit }
                        }
                        if (prevUnit === 'rem') {
                            const fontSize = getFontSizeByPageStyle() || 0
                            const gap = Number(((fontSize * prevGap * 100) / contentSize).toFixed(2))
                            return { size: gap, unit }
                        }
                        break
                    }

                    case 'rem': {
                        const fontSize = getFontSizeByPageStyle() || 0

                        if (prevUnit === '%') {
                            const gap = Number((Math.round((prevGap * contentSize) / 100) / fontSize).toFixed(2))
                            return { size: gap, unit }
                        }
                        if (prevUnit === 'px') {
                            const gap = Number((prevGap / fontSize).toFixed(2))
                            return { size: gap, unit }
                        }
                        break
                    }

                    case 'px': {
                        if (prevUnit === '%') {
                            const gap = Math.round((prevGap * contentSize) / 100)
                            return { size: gap, unit }
                        }
                        if (prevUnit === 'rem') {
                            const fontSize = getFontSizeByPageStyle() || 0
                            const gap = Math.round(prevGap * fontSize)
                            return { size: gap, unit }
                        }
                        break
                    }

                    default: {
                        break
                    }
                }

                return { size: prevGap, unit }
            },
            [direction]
        )
    )

    return (
        <LayoutSizeInput
            iconType="GapHorizontal"
            value={value}
            onChange={onChange}
            onChangeMove={setMoveOpen}
            onUnitChange={onUnitChange}
            disablePercent={disablePercent}
            disableRem={disableRem}
            inputProps={{
                wrapperProps: {
                    onMouseEnter: onMouseEnterGap,
                    onMouseLeave: onMouseLeaveGap
                }
            }}
        />
    )
}
